/**
 * O cartão do teto de desconto do Orçamento do CRM (13.22b1, FR-93) — Configuração → Negócios.
 *
 * **Um número só**: até quantos por cento de desconto sobre o total o orçamento sai "Pronto"; acima
 * dele, a foto nasce "Aguardando aprovação" (decisão do Gustavo, 26/10/2026). O teto é um só — nem
 * por perfil, nem por usuário, nem por forma de pagamento. Numa instalação nova ele nasce em **0**:
 * até a gestão configurar, todo desconto pede aprovação.
 *
 * **O molde é o da régua de dias** (`ReguaDeDias.tsx`, e as classes de `regua-de-dias.css`): o
 * cartão fica somente-leitura para quem não tem `administrar_listas_previsao_e_campos`, porque o
 * teto é configuração global — não existe "o meu teto". A recusa do serviço aparece **verbatim**, e
 * o que a pessoa digitou fica onde está. Depois de salvar, o teto é relido do serviço.
 *
 * **Quem decide a situação de cada orçamento é o serviço**, pelo teto gravado na foto no instante
 * em que ela nasceu: mudar o teto aqui não muda orçamento nenhum já gerado, e a nota do cartão diz
 * isso.
 *
 * **O cartão não existe para a SDR** — de fábrica ou derivada, o serviço a recusa com `403` nas duas
 * rotas (ela não vê desconto nem Orçamento do CRM, FR-62). Esconder é conveniência; quem recusa é o
 * serviço.
 *
 * **Paridade de celular (NFR-1).** O campo e o botão ficam lado a lado nas duas larguras: são um
 * campo estreito e um botão, e cabem nos 343 px úteis do celular; a linha quebra sozinha
 * (`flex-wrap` de `.regua__cortes`) se não couberem. Sem `@media` e sem árvore alternativa — a
 * classe `--empilhados` da régua daria 140 px de **altura** ao campo (o `flex-basis` em coluna).
 */

import { type FormEvent, useState } from 'react'

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import { useSessao } from '@/modulos/identidade/sessao'
import { type TetoDeDesconto as Teto, useConfigurarTetoDeDesconto, useTetoDeDesconto } from './api'
import { fraseDoTeto, textoDaTaxa } from './OrcamentoDoCrm'
import './regua-de-dias.css'

/** A permissão do FR-61-A que este cartão respeita (a mesma das formas de pagamento). */
const PERMISSAO_DE_CONFIGURAR = 'administrar_listas_previsao_e_campos'

export const TITULO_DO_TETO = 'Teto de desconto'
export const NOTA_DO_TETO =
  'Desconto do Orçamento do CRM, em percentual sobre o total. Mudar o teto não muda os orçamentos ' +
  'já gerados.'
export const ROTULO_DO_TETO = 'Teto (%)'
export const TETO_SALVO = 'Teto salvo.'
export const TETO_INVALIDO = 'Informe o teto em percentual, de 0 a 100, com até duas casas.'
const CARREGANDO = 'Carregando o teto…'

const ID_DO_TITULO = 'teto-de-desconto-titulo'
const ID_DO_CAMPO = 'teto-de-desconto-percentual'
const ID_DA_RECUSA = 'teto-de-desconto-recusa'

function mensagemDe(falha: unknown): string {
  return falha instanceof ErroDaApi ? falha.message : MENSAGEM_DE_ERRO_DESCONHECIDO
}

/**
 * O teto digitado, no formato do contrato (`"5.5"`, com ponto), ou `undefined` quando o texto não é
 * um percentual de 0 a 100 com até duas casas. Vazio também é `undefined`: o teto é obrigatório.
 */
export function tetoDoCampo(texto: string): string | undefined {
  const limpo = texto.trim().replace(',', '.')
  if (!/^\d{1,3}(\.\d{1,2})?$/.test(limpo)) return undefined
  if (Number(limpo) > 100) return undefined
  return limpo
}

/** O teto gravado, no campo: `"5.00"` vira `"5,00"`. */
function tetoNoCampo(percentual: string): string {
  return percentual.replace('.', ',')
}

/**
 * O formulário, semeado pelo teto que o serviço respondeu. Ele é remontado (pela `key`) quando o
 * teto muda — o molde do formulário da régua —, e a confirmação e a recusa do serviço moram no pai,
 * que sobrevive à remontagem.
 */
function FormularioDoTeto({
  teto,
  podeConfigurar,
  gravacao,
}: {
  teto: Teto
  podeConfigurar: boolean
  gravacao: ReturnType<typeof useConfigurarTetoDeDesconto>
}) {
  const [rascunho, setRascunho] = useState(tetoNoCampo(teto.percentual))
  const [recusaLocal, setRecusaLocal] = useState<string | null>(null)

  function enviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    gravacao.reset()
    const percentual = tetoDoCampo(rascunho)
    if (percentual === undefined) {
      setRecusaLocal(TETO_INVALIDO)
      return
    }
    setRecusaLocal(null)
    gravacao.mutate({ percentual })
  }

  /* A frase segue o rascunho enquanto ele é um teto; senão, mostra o que está valendo. */
  const doRascunho = tetoDoCampo(rascunho)
  const frase = fraseDoTeto(doRascunho ?? teto.percentual)

  return (
    <form className="regua__formulario" onSubmit={enviar} noValidate>
      <div className="regua__cortes">
        {podeConfigurar ? (
          <div className="regua__campo">
            <label className="regua__rotulo" htmlFor={ID_DO_CAMPO}>
              {ROTULO_DO_TETO}
            </label>
            <input
              id={ID_DO_CAMPO}
              className="regua__entrada"
              type="text"
              inputMode="decimal"
              value={rascunho}
              disabled={gravacao.isPending}
              aria-invalid={recusaLocal === null ? undefined : true}
              aria-describedby={recusaLocal === null ? undefined : ID_DA_RECUSA}
              onChange={(evento) => {
                setRecusaLocal(null)
                gravacao.reset()
                setRascunho(evento.target.value)
              }}
            />
          </div>
        ) : (
          /* Somente-leitura é texto, e não `<input disabled>`: o leitor de tela lê o valor. */
          <p className="regua__corte-lido">
            <span className="regua__rotulo">{ROTULO_DO_TETO}</span>
            <span className="regua__valor">{textoDaTaxa(teto.percentual)}</span>
          </p>
        )}
        {podeConfigurar ? (
          <div className="regua__acoes">
            <button className="regua__botao" type="submit" disabled={gravacao.isPending}>
              {gravacao.isPending ? 'Salvando…' : 'Salvar teto'}
            </button>
          </div>
        ) : null}
      </div>

      <p className="regua__ajuda">{frase}</p>

      {recusaLocal === null ? null : (
        <p className="regua__erro" id={ID_DA_RECUSA} role="alert">
          {recusaLocal}
        </p>
      )}

      {podeConfigurar ? null : (
        <p className="regua__ajuda">
          Só quem administra as listas de Negócios pode alterar o teto. Este é o teto que vale para
          todos os orçamentos.
        </p>
      )}
    </form>
  )
}

export function TetoDeDesconto() {
  const { data: sessao } = useSessao()
  const ehSdr = sessao?.perfil === 'sdr'
  const consulta = useTetoDeDesconto({
    habilitada: sessao !== undefined && !ehSdr,
  })
  const gravacao = useConfigurarTetoDeDesconto()

  if (sessao === undefined || ehSdr) return null

  /* `in`, e não comparação com `'concedida'`: a claim só carrega as não negadas (AD-5). */
  const podeConfigurar = PERMISSAO_DE_CONFIGURAR in sessao.permissoes
  const teto = consulta.data

  return (
    <section className="regua" aria-labelledby={ID_DO_TITULO}>
      <div className="regua__cartao">
        <div className="regua__cabecalho">
          <h2 className="regua__titulo" id={ID_DO_TITULO}>
            {TITULO_DO_TETO}
          </h2>
          <span className="regua__ajuda">{NOTA_DO_TETO}</span>
        </div>

        <div className="regua__corpo">
          {consulta.isError ? (
            /* Verbatim: a recusa do serviço é a que sabe explicar o caso. */
            <p className="regua__erro" role="alert">
              {mensagemDe(consulta.error)}
            </p>
          ) : consulta.isLoading || teto === undefined ? (
            <p className="regua__ajuda" role="status">
              {CARREGANDO}
            </p>
          ) : (
            <>
              <FormularioDoTeto
                key={teto.percentual}
                teto={teto}
                podeConfigurar={podeConfigurar}
                gravacao={gravacao}
              />
              {gravacao.isError ? (
                <p className="regua__erro" role="alert">
                  {mensagemDe(gravacao.error)}
                </p>
              ) : null}
              {gravacao.isSuccess ? (
                <p className="regua__sucesso" role="status">
                  {TETO_SALVO}
                </p>
              ) : null}
            </>
          )}
        </div>
      </div>
    </section>
  )
}
